package com.advaita.cloud.drive.ui.api

/**
 * ADV-1497 (S8-D1): sign-in keeps the Shared and My access view.
 *
 * The org console sends people to `/ui/?dialog=my-access`, `/ui/?view=shared` and
 * `/ui/?view=shared-by-me`. A person with no valid token goes through /auth/login and az2, and
 * the callback page always lands on bare `/ui/`, so the query was lost. Before the bounce the
 * web saves `pathname + search` in sessionStorage; on the next load of bare `/ui/` it gives the
 * value back once, for `history.replaceState`.
 *
 * No open redirect: the server and the OAuth flow carry no return path, and the value is
 * accepted only when it matches the allow-list exactly — path `/ui/`, and a query whose keys
 * are only `view` (`shared` | `shared-by-me`) and `dialog` (`my-access`), each at most once,
 * compared raw (no percent-decoding). `replaceState` is also same-origin by definition.
 *
 * Pure over a [KeyValueStore] so jsTest can run it under Node. Every storage access is guarded:
 * a storage failure means "nothing saved", never a failed sign-in (the ADV-443 rule).
 */
object SignInReturn {
    const val KEY = "drive.signInReturn"

    private const val LANDING_PATH = "/ui/"
    private val ALLOWED: Map<String, Set<String>> = mapOf(
        "view" to setOf("shared", "shared-by-me"),
        "dialog" to setOf("my-access"),
    )

    /**
     * Before a bounce to /auth/login. Saves the location when the allow-list accepts it, and
     * otherwise removes any old value, so a sign-in from a plain page lands on `/ui/` as before.
     */
    fun save(store: KeyValueStore?, pathname: String, search: String) {
        if (store == null) return
        val value = pathname + search
        runCatching {
            if (pathname.startsWith(LANDING_PATH) && isAllowed(value)) store.set(KEY, value)
            else store.remove(KEY)
        }
    }

    /**
     * On page load. Always removes the saved value (single use). Gives it back only when this
     * load is the bare `/ui/` landing page of the callback and the value passes the allow-list.
     */
    fun consume(store: KeyValueStore?, pathname: String, search: String): String? {
        if (store == null) return null
        val saved = runCatching { store.get(KEY) }.getOrNull() ?: return null
        runCatching { store.remove(KEY) }
        if (pathname != LANDING_PATH || search.isNotEmpty()) return null
        return saved.takeIf { isAllowed(it) }
    }

    /** True when [value] is exactly `/ui/?k=v[&k=v]` with allow-listed keys and values. */
    fun isAllowed(value: String): Boolean {
        val prefix = "$LANDING_PATH?"
        if (!value.startsWith(prefix)) return false
        val query = value.removePrefix(prefix)
        if (query.isEmpty()) return false
        val seen = mutableSetOf<String>()
        for (pair in query.split("&")) {
            val parts = pair.split("=")
            if (parts.size != 2) return false
            val (key, v) = parts
            val values = ALLOWED[key] ?: return false
            if (v !in values || !seen.add(key)) return false
        }
        return true
    }
}
